import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import BackendConnector from '../Utils/BackendConnector';
import Constant from '../Utils/Constant';

import Error500Page from '../Error500/Error500Page';
import NotFoundPage from '../NotFound/NotFoundPage';
import { usePhotoCarousel } from '../Utils/usePhotoCarousel';
import { processError, getErrorPageType } from '../Utils/ErrorHandler';

const AlbumPage = () => {
    // Carousel hook provides all carousel state and functions
    const { carouselOpen, carouselIndex, carouselPhotos, carouselRef, handleTouchStart, handleTouchMove, handleTouchEnd, openCarousel, closeCarousel, prevPhoto, nextPhoto, setCarouselPhotos } = usePhotoCarousel();
    
    const { albumId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [album, setAlbum] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorStatus, setErrorStatus] = useState(null);

    useEffect(() => {
        const fetchAlbumData = async () => {
            try {
                setIsLoading(true);
                
                // Récupération des détails de l'album
                const albumData = await BackendConnector.fetchData(`albums/${albumId}/`);
                setAlbum(albumData);
                
                // Récupération des photos de l'album
                const photosData = await BackendConnector.fetchData(`albums/${albumId}/photos/`);
                setPhotos(photosData);
                // Initialize carousel photos with album photos
                if (photosData && photosData.length > 0) {
                    setCarouselPhotos(photosData.sort((a, b) => a.order - b.order));
                }
                
            } catch (err) {
                console.error('Erreur lors du chargement de l\'album:', err);
                const { error: errorMsg, status } = processError(err, Constant.UNABLE_TO_LOAD_ALBUM);
                setErrorStatus(status);
                setError(errorMsg);
            } finally {
                setIsLoading(false);
            }
        };

        if (albumId) {
            fetchAlbumData();
        }
    }, [albumId]);

    const handleBackClick = () => {
        // Si on vient d'une PlacePage, on y retourne
        if (location.state?.from === 'place') {
            navigate(`/travel/${location.state.countryId}/place/${location.state.placeId}`);
        } else if (location.state?.from === 'hiking') {
            // Si on vient d'une HikingPage, on y retourne
            navigate(`/hiking/${location.state.hikingId}`);
        } else {
            // Sinon, on retourne à la galerie sur la page d'accueil
            navigate('/');
            setTimeout(() => {
                const galleryElement = document.getElementById('gallery');
                if (galleryElement) {
                    galleryElement.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        }
    };

    if (isLoading) {
        return (
            <div className="not-found-page">
                <div className="not-found-content">
                    <div className="spinner-border text-light" role="status" style={{fontSize: '2rem'}}>
                        <span className="visually-hidden">{Constant.LOADING}</span>
                    </div>
                    <p className="not-found-message mt-3">{Constant.LOADING_ALBUM}</p>
                </div>
            </div>
        );
    }

    if (error || !album) {
        // Si c'est une erreur 500, afficher la page d'erreur 500
        if (errorStatus === 500) {
            return <Error500Page />;
        }
        
        // Sinon, afficher la page 404 (pour les erreurs 404 ou autres)
        // Déterminer le message utilisateur à afficher (ALBUM_NOT_FOUND si erreur 404, sinon le message d'erreur technique)
        let userMessage = Constant.ALBUM_NOT_FOUND;
        let technicalMessage = null;
        if (error && (error.includes('404') || error.toLowerCase().includes('not found'))) {
            userMessage = Constant.ALBUM_NOT_FOUND;
            technicalMessage = null;
        } else if (error) {
            userMessage = error;
            technicalMessage = error;
        }
        return (
            <NotFoundPage
                title={"404"}
                message={Constant.ERROR}
                description={userMessage}
                technicalMessage={technicalMessage}
                buttonLabel={Constant.BACK_TO_GALLERY}
                onBack={handleBackClick}
                iconClass="fas fa-arrow-left me-2"
            />
        );
    }

    return (
        <div className="album-page">
            <div className="container-fluid">
                {/* Header de l'album */}
                <div 
                    className="album-header"
                    style={{
                        backgroundImage: album.cover ? `url(${album.cover})` : 'none'
                    }}
                >
                    <div className="album-header-overlay">
                        <button className="btn mb-3" onClick={handleBackClick}>
                            <i className="fas fa-arrow-left"></i>
                            {Constant.BACK_TO_GALLERY}
                        </button>
                        <div className="album-title-section">
                            <h1 className="album-page-title">{album.title}</h1>
                            <div className="album-meta-info">
                                {album.category_name && (
                                    <span className="album-type-badge">
                                        {album.category_name}
                                    </span>
                                )}
                                {album.country && (
                                    <span className="album-country-badge">
                                        📍 {album.country}
                                    </span>
                                )}
                            </div>
                            {album.description && (
                                <p className="album-description">{album.description}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Galerie de photos */}
                <div className="photos-gallery">
                    {photos.length === 0 ? (
                        <div className="text-center">
                            <p className="text-muted">{Constant.NO_PHOTOS_IN_ALBUM}</p>
                        </div>
                    ) : (
                        <div className="row g-3">
                            {photos.map((photo, index) => (
                                <div key={photo.id} className="col-lg-3 col-md-4 col-sm-6 col-12">
                                    <div 
                                        className="photo-card"
                                        onClick={() => openCarousel(index, photos.sort((a, b) => a.order - b.order))}
                                    >
                                        <img 
                                            src={photo.thumb_url || photo.url} 
                                            alt={photo.title || `${Constant.PHOTO} ${index + 1}`}
                                            className="photo-image"
                                            loading="lazy"
                                        />
                                        {photo.title && (
                                            <div className="photo-overlay">
                                                <p className="photo-title">{photo.title}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Carrousel modale */}
                {carouselOpen && carouselPhotos?.length > 0 && (
                    <div
                        ref={carouselRef}
                        className="carousel-fullscreen"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <button 
                            className="carousel-close-btn"
                            onClick={closeCarousel}
                            aria-label="Fermer la galerie"
                            title="Fermer"
                        >
                            ✕
                        </button>

                        <div className="carousel-container">
                            <img 
                                src={carouselPhotos?.[carouselIndex]?.url} 
                                alt={carouselPhotos?.[carouselIndex]?.title || `Photo ${carouselIndex + 1}`}
                                className="carousel-image"
                            />
                            
                            <button 
                                className="carousel-btn prev-btn"
                                onClick={prevPhoto}
                                aria-label="Photo précédente"
                                title="Photo précédente"
                            >
                                ←
                            </button>
                            
                            <button 
                                className="carousel-btn next-btn"
                                onClick={nextPhoto}
                                aria-label="Photo suivante"
                                title="Photo suivante"
                            >
                                →
                            </button>
                        </div>

                        <div className="carousel-controls">
                            <span className="carousel-counter">
                                {carouselIndex + 1} / {carouselPhotos?.length}
                            </span>
                        </div>
                        
                        {carouselPhotos?.[carouselIndex]?.title && (
                            <div className="carousel-title">
                                {carouselPhotos[carouselIndex].title}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AlbumPage;